import { memo, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { CompanyLogo } from '@hotjar/navigation/src/components/CompanyLogo';
import { SideBar } from '@hotjar/navigation/src/components/SideBar';
import { TopBar } from '@hotjar/navigation/src/components/TopBar';
import { StickyBanners } from '@hotjar/navigation/src/components/StickyBanners';
import { AccountMenu } from '@hotjar/navigation/src/components/AccountMenu';
import { HelpMenu } from '@hotjar/navigation/src/components/HelpMenu';
import { IntegrationsLink } from '@hotjar/navigation/src/components/IntegrationsLink';
import { InviteMembers } from '@hotjar/navigation/src/components/InviteMembers';
import { SiteSelector } from '@hotjar/navigation/src/components/SiteSelector';
import { attachHandlers } from '@hotjar/navigation/src/events/attachHandlers';
import { createEmitter } from '@hotjar/navigation/src/events/createEmitter';
import { Position } from '@hotjar/navigation/src/events/Position';

import type { NavigationProps, PageType } from '@hotjar/navigation/src/types';

import { useNavigation } from '@hotjar/navigation/src/hooks/useNavigation';

import { getPageType } from './utils';
import { useIsScreenNarrow } from '@hotjar/navigation/src/hooks/useIsScreenNarrow';
import {
  SettingsSidebarList,
  FeaturesSidebarList,
  ProfileSidebarList,
} from '@hotjar/navigation/src/components/SidebarList/';

import { HEADER_ZINDEX, SIDEBAR_ZINDEX } from '@hotjar/navigation/src/constants';
import { EngageActivationNeededModal, EngageTCModal, useEngageButton } from '@hotjar/auth';

import { LanguageSelector } from '@hotjar/translations';
import { GettingStartedItem } from '../../components/GettingStartedItem';

const TopBarContainer = styled.header`
  position: sticky;
  top: 0;
  z-index: ${HEADER_ZINDEX};
`;

const StyledSideBar = styled(SideBar)<{ topbarHeight: number }>`
  position: fixed;
  bottom: 0;
  top: ${({ topbarHeight }) => topbarHeight}px;
  z-index: ${SIDEBAR_ZINDEX};
`;

/**
 * Top container of the Navigation. Only place where we get data from legacy
 */
const InternalNavigation = ({
  currentSite,
  currentUser,
  getOrgsAndSites,
  currentRouteConfig,
  access,
  isAccountUpgradable,
  hideSidebar,
  hideSiteWidgets,
  whatsNewCount,
  emitter: passedEmitter,
  hotjarTracking,
  trackClick,
  sidebarNotifications,
  topBarMessage,
  samplingWidget,
  hasEngageCreateTeam = false,
  hasEngageAccessTeam = false,
  shouldShowExtendedLanguageSwitcher = false,
  demoSiteSelectorConfig,
  gsgSideBarExperimentConfig,
  showPublicApi,
}: NavigationProps): JSX.Element | null => {
  const isScreenNarrow = useIsScreenNarrow();
  const {
    isSidebarMinimized,
    setIsSidebarMinimized,
    setIsSidebarHidden,
    topBarHeight,
    routes,
    buildHrefFromRoute,
  } = useNavigation();

  const {
    onClick: onEngageButtonClick,
    acceptTermsAndConditions,
    isTCModalOpen,
    setTCModalState,
    isNeedActivationModalOpen,
    setNeedActivationModalState,
    accountAdminName,
  } = useEngageButton(hasEngageAccessTeam, hasEngageCreateTeam, access.canAccessEngageInsights);

  const [lastManualSidebarCollapse, setLastManualSidebarCollapse] = useState(isSidebarMinimized);

  useEffect(() => {
    // this should be set from the prop passed in, so that it can be read elsewhere
    setIsSidebarHidden(hideSidebar ?? false);
  }, [hideSidebar, setIsSidebarHidden]);

  useEffect(() => {
    // with a narrow screen the sidebar is always minimized.
    // otherwise it picks up the last manual change (collapse/expand) the user chose
    setIsSidebarMinimized(isScreenNarrow || lastManualSidebarCollapse);
  }, [isScreenNarrow, lastManualSidebarCollapse, setIsSidebarMinimized]);

  const emitter = createEmitter(passedEmitter);

  useEffect(() => {
    attachHandlers({ emitter, hotjarTracking, trackClick });
  }, [emitter, hotjarTracking, trackClick]);

  // if no routes available, don't render
  if (Object.keys(routes).length === 0) {
    return null;
  }

  const pageType: PageType = getPageType(currentRouteConfig?.name);

  const urlBuilderConfig = { siteId: currentSite.id };

  const accountId = currentSite.account.id;
  const orgId = currentSite.organizationId;
  const siteId = currentSite.id;

  const onOpenEngage = () => onEngageButtonClick(accountId, orgId, siteId);

  return (
    <>
      <TopBarContainer>
        <TopBar
          logo={
            <CompanyLogo
              siteDashboardOverviewUrl={
                currentSite.id
                  ? buildHrefFromRoute(routes.OVERVIEW.BASE.path, urlBuilderConfig)
                  : ''
              }
              companyLogoUrl={currentSite.account?.pictureUrl}
              onClick={() => {
                emitter.emit('clickedLogo', { position: Position.primaryNavigation });
              }}
            />
          }
          hideSiteWidgets={hideSiteWidgets}
          siteSelector={
            <SiteSelector
              demoSiteSelectorConfig={demoSiteSelectorConfig}
              emitter={emitter}
              getOrgsAndSites={getOrgsAndSites}
              currentRouteConfig={currentRouteConfig}
              currentSite={currentSite}
              userId={currentUser.id}
            />
          }
          topBarMessage={topBarMessage}
          samplingWidget={samplingWidget}
          integrationsLink={
            <IntegrationsLink
              onClick={() => {
                emitter.emit('clickedIntegrations', { position: Position.primaryNavigation });
              }}
            />
          }
          inviteMembers={
            <InviteMembers
              onClick={() => {
                emitter.emit('clickedInviteMembers', { position: Position.primaryNavigation });
              }}
            />
          }
          helpMenu={
            gsgSideBarExperimentConfig.isSideBarEnabled ? (
              <GettingStartedItem
                toggleSideBar={gsgSideBarExperimentConfig.toggleSideBar}
                emitter={emitter}
                isSideBarOpen={gsgSideBarExperimentConfig.isOpen}
                remainingTasks={gsgSideBarExperimentConfig.remainingTasks}
              />
            ) : (
              <HelpMenu emitter={emitter} whatsNewCount={whatsNewCount} currentSite={currentSite} />
            )
          }
          accountMenu={
            <AccountMenu
              emitter={emitter}
              name={currentUser.name}
              email={currentUser.email}
              avatar={currentUser.avatar}
              isAccountUpgradable={isAccountUpgradable}
              isEngageButtonActive={!access.canAccessEngageInsights && hasEngageAccessTeam}
              onOpenEngage={onOpenEngage}
            />
          }
          languageSelector={
            shouldShowExtendedLanguageSwitcher ? (
              <LanguageSelector shouldUseLocalizationDevMode userId={currentUser.id} />
            ) : null
          }
        />
        <StickyBanners />
      </TopBarContainer>

      {!hideSidebar && (
        <StyledSideBar
          topbarHeight={topBarHeight}
          isCollapsed={isSidebarMinimized}
          showCollapseToggle={!isScreenNarrow}
          onCollapseClick={() => {
            emitter.emit(isSidebarMinimized ? 'clickedSidebarExpand' : 'clickedSidebarCollapse', {
              position: Position.navToolbar,
            });
            setIsSidebarMinimized(!isSidebarMinimized);
            setLastManualSidebarCollapse(!isSidebarMinimized);
          }}
        >
          {pageType === 'settings' && (
            <SettingsSidebarList
              isCollapsed={isSidebarMinimized}
              emitter={emitter}
              currentRouteConfig={currentRouteConfig}
              sidebarNotifications={sidebarNotifications}
              showPreferences={currentUser?.isAccountAdmin}
              showInvoices={access.canAccessInvoices}
              showSubscriptions={access.canAccessSubscriptions}
              showPlansAndBilling={access.canAccessPlansAndBilling}
              showPublicApi={showPublicApi}
            />
          )}
          {pageType === 'profile' && (
            <ProfileSidebarList
              isCollapsed={isSidebarMinimized}
              emitter={emitter}
              currentRouteConfig={currentRouteConfig}
              sidebarNotifications={sidebarNotifications}
              userName={currentUser.name}
            />
          )}
          {pageType === 'feature' && (
            <FeaturesSidebarList
              isCollapsed={isSidebarMinimized}
              emitter={emitter}
              currentRouteConfig={currentRouteConfig}
              onOpenEngage={onOpenEngage}
              siteId={currentSite.id}
              sidebarNotifications={sidebarNotifications}
              hasEngageAccessTeam={hasEngageAccessTeam}
            />
          )}
        </StyledSideBar>
      )}

      <EngageTCModal
        isOpen={isTCModalOpen}
        onClose={() => setTCModalState(false)}
        onSubmit={() => acceptTermsAndConditions(accountId, orgId, siteId)}
      />

      <EngageActivationNeededModal
        trackData={{ accountId, orgId, siteId }}
        isOpen={isNeedActivationModalOpen}
        onClose={() => setNeedActivationModalState(false)}
        accountAdminName={accountAdminName}
      />
    </>
  );
};

export const Navigation = memo(InternalNavigation);
